import { createStore } from "@xstate/store";
import { useSelector } from "@xstate/store-react";
import type { OverlayKind, OverlayProps } from "./registry";

export interface OverlayEntry<K extends OverlayKind = OverlayKind> {
  id: string;
  kind: K;
  props: OverlayProps[K];
  /** False while the exit animation runs; the entry leaves on `dismiss`. */
  open: boolean;
}

export const overlayStore = createStore({
  context: { stack: [] as OverlayEntry[] },
  on: {
    open: (ctx, event: { entry: OverlayEntry }) => ({ stack: [...ctx.stack, event.entry] }),
    close: (ctx, event: { id: string }) => ({
      stack: ctx.stack.map((o) => (o.id === event.id ? { ...o, open: false } : o)),
    }),
    dismiss: (ctx, event: { id: string }) => ({ stack: ctx.stack.filter((o) => o.id !== event.id) }),
  },
});

// Addresses a row in the stack for its lifetime and nothing else — never
// persisted, never sent anywhere. A counter is all the uniqueness it needs.
let nextEntryId = 0;

export function openOverlay<K extends OverlayKind>(kind: K, props: OverlayProps[K]): string {
  const id = `overlay-${++nextEntryId}`;
  overlayStore.send({ type: "open", entry: { id, kind, props, open: true } });
  return id;
}

export const closeOverlay = (id: string) => overlayStore.send({ type: "close", id });
export const dismissOverlay = (id: string) => overlayStore.send({ type: "dismiss", id });

export const useOverlayStack = () => useSelector(overlayStore, (s) => s.context.stack);
